import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import './index.css';
import App from './App';
import FlowEditor from './components/FlowEditor';
import EmbeddedFlowEditor from './components/EmbeddedFlowEditor';
import OutlineView from './components/OutlineView';

// Store references to roots for cleanup
let currentRoot: Root | null = null;
let embeddedRoot: Root | null = null;

// Mount function for modal flow editor (original)
function mount(container: HTMLElement, props: {
  tareaId?: string;
  embedded?: boolean;
  onClose?: () => void;
  parseConfig?: {
    appId: string;
    serverURL: string;
    sessionToken?: string;
  };
}) {
  // Cleanup previous root if exists
  if (currentRoot) {
    currentRoot.unmount();
  }

  currentRoot = createRoot(container);
  currentRoot.render(
    <StrictMode>
      <OutlineView onClose={props.onClose} />
    </StrictMode>
  );

  return {
    unmount: () => {
      if (currentRoot) {
        currentRoot.unmount();
        currentRoot = null;
      }
    }
  };
}

// Mount function for embedded inline flow editor (NEW)
function mountEmbedded(container: HTMLElement, props: {
  onNodeClick?: (codigoUnico: string) => void;
  selectedCodigoUnico?: string | null;
}) {
  // Cleanup previous root if exists
  if (embeddedRoot) {
    embeddedRoot.unmount();
  }

  embeddedRoot = createRoot(container);
  embeddedRoot.render(
    <StrictMode>
      <EmbeddedFlowEditor {...props} />
    </StrictMode>
  );

  return {
    unmount: () => {
      if (embeddedRoot) {
        embeddedRoot.unmount();
        embeddedRoot = null;
      }
    },
    // Update props without remounting
    updateSelection: (codigoUnico: string | null) => {
      if (embeddedRoot) {
        embeddedRoot.render(
          <StrictMode>
            <EmbeddedFlowEditor {...props} selectedCodigoUnico={codigoUnico} />
          </StrictMode>
        );
      }
    },
    // Trigger a rebuild from DOM
    refresh: () => {
      window.dispatchEvent(new CustomEvent('acciones-updated'));
    }
  };
}

// Export for library mode - this becomes the global LuukFlowEditor
const LuukFlowEditor = {
  mount,
  mountEmbedded,
  FlowEditor,
  EmbeddedFlowEditor,
};

// Also set on window explicitly for safety
if (typeof window !== 'undefined') {
  (window as any).LuukFlowEditor = LuukFlowEditor;
}

// Export as default for library mode
export default LuukFlowEditor;

// Named exports
export { mount, mountEmbedded, FlowEditor, EmbeddedFlowEditor };
export type * from './types';

// Mount app if root element exists (for standalone mode)
const rootElement = document.getElementById('root');
if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <App />
    </StrictMode>
  );
}
